<template>
  <form @submit.prevent="handleSubmit" class="stripe-payment-form">
    <div v-if="!isCreditCardPresent">
      <div class="field">
        <label for="card-number" class="stripe-payment-form-label">
          Card Number <span class="required">*</span>
        </label>
        <div id="card-number" class="stripe-element"></div>
        <span class="error" v-if="cardNumberError">{{ error }}</span>
      </div>
      <div class="flex-container">
        <div class="field flex-item">
          <label for="card-expiry" class="stripe-payment-form-label">
            Expiration Date <span class="required">*</span>
          </label>
          <div id="card-expiry" class="stripe-element"></div>
          <span class="error" v-if="cardExpiryError">{{ error }}</span>
        </div>
        <div class="field flex-item">
          <label for="card-cvc" class="stripe-payment-form-label">
            Security Code <span class="required">*</span>
          </label>
          <div id="card-cvc" class="stripe-element"></div>
          <span class="error" v-if="cardCVVError">{{ error }}</span>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="existing-credit-card-main-wrapper">
        <span class="existing-credit-card-label">Card Number</span><span class="error"> *</span>
        <div class="existing-credit-card-child-wrapper">
          <div class="existing-creditcard-info">
            <span class="masked-card">
              <span class="dot-group">••••••••</span>
              <span class="last-4-Digit"> {{ this.creditCardData.cardNumber }}</span>
            </span>
          </div>
          <div class="change-credit-card">
            <div class="change-credit-button-container">
              <mds-button-container>
                <mds-button variation="secondary" size="small" @click="ChangeCard()" :disabled="processing">
                  Change Payment Method
                </mds-button>
              </mds-button-container>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>
  
<script>
  import { loadStripe } from '@stripe/stripe-js';
  import { postData } from "@/utilities/API/apiUtility";
  import { MdsButton, MdsButtonContainer } from "@mds/button";
  export default {
    name: 'PaymentForm',
    components: {
      MdsButton,
      MdsButtonContainer,
    },
    props: {
    paymentData: {
      type: Object,
      required: true
    },
    creditCardData: {
      type: Object,
      default: () => ({})
    },
    processing: {
      type: Boolean,
      default: false,
    }
  },
    data() {
      return {
        stripe: null,
        elements: null,
        cardNumberElement: null,
        cardExpiryElement: null,
        cardCvcElement: null,
        //processing: false,
        error: null,
        cardNumberError:false,
        cardCVVError:false,
        cardExpiryError:false,
        success: false,
        isCardNumberValid: false,
        isCardExpiryValid: false,
        isCardCvcValid: false,
        baseURL:process.env.VUE_APP_BackEndAPI,
        stripePublicKey: process.env.VUE_APP_STRIPE_PK,
        paymentIntentURL: process.env.VUE_APP_BackEndAPI + process.env.VUE_APP_PAYMENT_INTENT,
        clientSecret:null,
        isCreditCardPresent : false,
        paymentMethodId:null,
      };
    },
    computed: {
      allValid() {
        return (
          this.isCardNumberValid &&
          this.isCardExpiryValid &&
          this.isCardCvcValid
        );
      },
    },
    watch: {
      allValid(newVal) {
        this.$emit("validity-changed", newVal);
      },
    },
    async mounted() {
      await this.generateStripeIFrame();
      if (this.creditCardData && Object.keys(this.creditCardData).length > 0) {
        this.isCreditCardPresent = true;
        this.$emit("validity-changed", true);
      } else {
        this.isCreditCardPresent = false;
        this.$emit("validity-changed", false);
      }
    },
    methods: {
      setupStripeListeners() {
        this.cardNumberElement.on("change", this.onCardNumberChange);
        this.cardExpiryElement.on("change", this.onCardExpiryChange);
        this.cardCvcElement.on("change", this.onCardCvcChange);
      },
      onCardNumberChange(event) {
        this.isCardNumberValid = event.complete && !event.error;
        this.error = event.error ? event.error.message : null;
        if (event.complete && !event.error) {
          this.cardExpiryElement.focus();
        }
        this.cardNumberError = this.error ? true : false;
      },
      onCardExpiryChange(event) {
        this.isCardExpiryValid = event.complete && !event.error;
        this.error = event.error ? event.error.message : null;
        if (event.complete && !event.error) {
          this.cardCvcElement.focus();
        }
        this.cardExpiryError = this.error ? true : false;
      },
      onCardCvcChange(event) {
        this.isCardCvcValid = event.complete && !event.error;
        this.error = event.error ? event.error.message : null;
        this.cardCVVError = this.error ? true : false;
      },
      async handleSubmit(){
        this.processing = true;
        this.error = null;
        this.success = false;

        this.$emit("payment-state", false);
        this.$emit("payment-failed", { flag: false, message: "" });
        this.$emit("processing-changed", true);
        
        const userFullName = this.paymentData.userDetails.metadata.mstar_fname + ' ' + this.paymentData.userDetails.metadata.mstar_lname;
        if (!this.isCreditCardPresent) {
          const { paymentMethod, error: pmError } = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardNumberElement,
            billing_details: {
              "address": {
                "city": this.paymentData.userDetails.address.city,
                "country": this.paymentData.userDetails.address.country,
                "line1": this.paymentData.userDetails.address.line1,
                "postal_code": this.paymentData.userDetails.address.postalCode,
                "state": this.paymentData.userDetails.address.state
              },
              name: userFullName,
              email: this.paymentData.userDetails.email,
              phone: this.paymentData.userDetails.mobilePhone && this.paymentData.userDetails.mobilePhone.trim() !== ''
                ? this.paymentData.userDetails.mobilePhone
                : this.paymentData.userDetails.metadata.home_phone
            },
          });
          this.paymentMethodId = paymentMethod.id;
          if (pmError) {
            this.resetFlags();
            this.error = pmError.message;
            this.$emit("payment-failed", { flag: true, message: pmError.message });
            return;
          }else{
            const isCardAdded = await this.createDefaultCardForUser();
            await this.emitResponses(isCardAdded);
          }
        }else{
          this.$emit("processing-changed", false);
          this.$emit("payment-state", true);
        }
      },
      async createDefaultCardForUser(){
        try{
          const response = await postData(this.paymentIntentURL, {
            paymentMethodId: this.paymentMethodId,
            customerId: this.paymentData.userDetails.id
          });
          let reponseObject = {} ;
          if (response && response.data.message.includes("marked as default successfully")) {
            reponseObject = {
              message : response.data.message,
              flag : true
            };
            return reponseObject;

          }else{
            reponseObject = {
              message : response.data.message,
              flag : false
            };
            return reponseObject;
          }
        }catch (error) {
          this.resetFlags();
          this.error = error.message;
          this.$emit("payment-failed", { flag: true, message: error.message });
        }
      },
      async emitResponses(isCardAdded){
        if (isCardAdded.flag) {
            //EMit success message and start creating user subscription
            this.processing = false;
            this.$emit("processing-changed", false);

            this.$emit("payment-failed", { flag: false, message: "" });

            this.success = true;
            this.$emit("payment-state", true);

            await this.clearElements();

          }else{
            this.processing = false;
            this.$emit("processing-changed", false);

            this.$emit("payment-state", false);

            this.$emit("payment-failed", { flag: true, message: isCardAdded.message });
            //For logging purpose in dev env
            if (process.env.NODE_ENV == 'development') {
                  throw new Error('Error while creating default card');
            }
            
          }
      },
      async clearElements() {
        this.cardNumberElement.clear();
        this.cardExpiryElement.clear();
        this.cardCvcElement.clear();
        this.isCardNumberValid = false;
        this.isCardExpiryValid = false;
        this.isCardCvcValid = false;
        this.processing = false;
        this.success = false;
        this.error = null;
        // this.$emit("validity-changed", false);
        // this.$emit("payment-state", false);
        // this.$emit("payment-failed", false);
      },
      resetFlags() {
        this.processing = false;
        this.success = false;
        this.error = null;

        this.$emit("processing-changed", false);
        this.$emit("payment-state", false);
        this.$emit("payment-failed", { flag: false, message: "" });
      },
      async ChangeCard(){
        this.isCreditCardPresent = false;
        this.$emit("validity-changed", false);
        this.generateStripeIFrame();
      },
      async generateStripeIFrame() {
        this.stripe = await loadStripe(this.stripePublicKey);
        this.elements = this.stripe.elements();

        const style = {
          base: {
            fontFamily: '"MorningstarIntrinsic, HelveticaNeue, Helvetica Neue, Helvetica, Arial,sans-serif"',
            fontSize: '16px',
            color: '#32325d',
            '::placeholder': { color: '#aab7c4' }
          }
        };

        this.cardNumberElement = this.elements.create('cardNumber', {
          style,
          showIcon: true,
          iconStyle: 'default'
        });
        this.cardNumberElement.mount("#card-number");

        this.cardExpiryElement = this.elements.create("cardExpiry", { style });
        this.cardExpiryElement.mount("#card-expiry");

        this.cardCvcElement = this.elements.create("cardCvc", { style });
        this.cardCvcElement.mount("#card-cvc");
        this.setupStripeListeners();
      }
    },
  };
</script>
  
<style scoped>
  .stripe-payment-form{
    margin-top:2%;
  }
  .stripe-payment-form-label{
    font-size:16px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
    display: block;
    margin-bottom: 2px;
  }
  .field {
    margin-bottom: 15px;
  }
  .flex-container {
    display: flex;
    gap: 17px;
  }
  .flex-item {
    flex: 1;
  }
  .stripe-element {
    border: none;
    border-radius: 0;
    background-color: #E5E5E5;
    padding: 10px;
    background-clip: padding-box;
    box-shadow: inset 0 -1px 0 0 #808080;
    color: #1e1e1e;
  }
  .required {
    color: #F00;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
  }
  .error {
    color: #F00;
    font-size:14px;
  }
  .success {
    color: green;
  }
  .status-message{
    margin-bottom: 10px;
  }
  .existing-credit-card-main-wrapper{
    width:100%;
    height:76px;
  }
  .existing-credit-card-child-wrapper {
    display: flex;
    align-items: center;
  }
  .existing-creditcard-info{
    flex:1
  }

  .change-credit-button-container{
    margin-left:auto;
  }
  .existing-credit-card-label{
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 21px;
  }

  .masked-card {
    font-size: 20px;
    font-style: normal;
    font-weight: 300;
    line-height: 26px;
  }

  .dot-group {
    color: #5E5E5E;
  }

  .last-4-Digit {
    color: #1E1E1E;
  }

  
</style>
<style>
.existing-credit-card-child-wrapper>div:nth-child(1){
  
    z-index: -1;
  }
</style>

